// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { Show, type JSX } from "solid-js";
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  useLocation,
} from "@tanstack/solid-router";
import { AccountSettings } from "./accounts";
import { DefaultShell } from "./components/layout";
import { EntitlementsProvider } from "./entitlements";
import { GroupsPage } from "./groups";
import { Page } from "./infrastructure";
import { NotificationsPage } from "./notifications";
import { UserProfile } from "./profile";
import { ProjectsPage } from "./projects";
import { DefaultPage, NotFound } from "./public-pages";
import { AccountShell } from "./shell";

function AccountPage(p: { slot: number; account: string; rest: string[] }) {
  return p.rest[0] === "groups" ? (
    <GroupsPage {...p} />
  ) : p.rest[0] === "projects" ? (
    <ProjectsPage {...p} />
  ) : p.rest[0] === "settings" ? (
    <AccountSettings slot={p.slot} account={p.account} />
  ) : p.rest[0] === "notifications" ? (
    <NotificationsPage slot={p.slot} account={p.account} />
  ) : p.rest[0] === "profile" ? (
    <UserProfile slot={p.slot} />
  ) : (
    <Page {...p} />
  );
}

function DefaultRoute() {
  return (
    <DefaultShell>
      <DefaultPage />
    </DefaultShell>
  );
}

function ApplicationFrame(p: { children: JSX.Element }) {
  const location = useLocation();
  const match = () => location().pathname.match(/^\/u(\d+)\/([^/]+)/);
  const slot = () => Number(match()?.[1]);
  const accountKey = () =>
    Number.isSafeInteger(slot()) && slot() >= 0 && slot() <= 9
      ? `${slot()}:${match()![2]}`
      : undefined;
  return (
    <Show
      when={accountKey()}
      keyed
      fallback={p.children}
    >
      {(_accountKey) => (
        <AccountShell slot={slot()} account={match()![2]!}>
          {p.children}
        </AccountShell>
      )}
    </Show>
  );
}

function AccountIndex() {
  const location = useLocation();
  const match = () => location().pathname.match(/^\/u(\d+)\/([^/]+)/)!;
  return (
    <AccountPage
      slot={Number(match()[1])}
      account={match()[2]!}
      rest={[]}
    />
  );
}

function AccountContext() {
  const location = useLocation();
  const match = () => location().pathname.match(/^\/u(\d+)\/([^/]+)/)!;
  const accountKey = () => `${match()[1]}:${match()[2]}`;
  return (
    <Show when={accountKey()} keyed>
      {(_accountKey) => {
        const slot = Number(match()[1]);
        return Number.isSafeInteger(slot) && slot >= 0 && slot <= 9 ? (
          <EntitlementsProvider slot={slot} account={match()[2]!}>
            <Outlet />
          </EntitlementsProvider>
        ) : (
          <DefaultShell>
            <NotFound />
          </DefaultShell>
        );
      }}
    </Show>
  );
}

function AccountCatchall() {
  const location = useLocation();
  const match = () =>
    location().pathname.match(/^\/u(\d+)\/([^/]+)\/(.*)$/)!;
  return (
    <Show when={match()[3]} keyed>
      {(path) => (
        <AccountPage
          slot={Number(match()[1])}
          account={match()[2]!}
          rest={path.split("/").filter(Boolean)}
        />
      )}
    </Show>
  );
}

const root = createRootRoute({ component: Outlet });
const accountRoute = createRoute({
  getParentRoute: () => root,
  path: "u{$slot}/$account",
  component: AccountContext,
});
const accountIndexRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "/",
  component: AccountIndex,
});
const accountCatchallRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "$",
  component: AccountCatchall,
});
const defaultIndexRoute = createRoute({
  getParentRoute: () => root,
  path: "/",
  component: DefaultRoute,
});
const defaultCatchallRoute = createRoute({
  getParentRoute: () => root,
  path: "$",
  component: DefaultRoute,
});
export const router = createRouter({
  InnerWrap: ApplicationFrame,
  routeTree: root.addChildren([
    accountRoute.addChildren([accountIndexRoute, accountCatchallRoute]),
    defaultIndexRoute,
    defaultCatchallRoute,
  ]),
});
